import * as Sentry from '@sentry/react'
import { ServerResponseProvider } from './View/Component/Provider/ServerFeedbackProvider.js'
import { OrganizationProvider } from './View/Page/OrderManagementPage/OrganizationProvider.js'
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useParams } from 'react-router-dom'
import { LoginPage } from './View/Page/LoginPage/LoginPage.js'
import { HomePage } from './View/Page/HomePage/HomePage.js'
import { OrderManagementPage } from './View/Page/OrderManagementPage/OrderManagementPage.js'
import { ProtectedRoute } from './View/Layout/ProtectedRoute/ProtectedRoute.js'
import Actions from './Flux/index.js'
import { ProjectManagementPage } from './View/Page/ProjectPage/ProjectManagementPage.js'
import { ProjectParamLoaderView } from './View/Page/ProjectPage/ProjectParamLoaderView.js'
import { OrderParamLoaderView } from './View/Page/OrderManagementPage/OrderParamLoaderView.js'
import { InvitesPage } from './View/Page/InvitePage/InvitesPage.js'
import { InviteDetailPage } from './View/Page/InvitePage/InviteDetailPage.js'
import { OrganizationPage } from './View/Page/OrganizationPage/OrganizationPage.js'
import { OrganizationSearchPage } from './View/Page/OrganizationSearchPage/OrganizationSearchPage.js'
import { ConfirmPostprocessingPage } from './View/Page/OrderManagementPage/ConfirmPostprocessingPage.js'
import { lazy, Suspense, useEffect } from 'react'
import { connectToServer } from './Flux/Slice/Application/ApplicationSlice.js'
import { Environment } from './environment.js'
import { useDispatch } from 'react-redux'
import { useGetAuthenticatedUser } from './Flux/Selector/index.js'
import { authenticationSlice } from './Flux/Slice/Authentication/AuthenticationSlice.js'
import { OtuDataTableView } from './View/Page/ProjectPage/OtuDataTableView.js'
import { ChatPage } from './View/Page/ChatPage/ChatPage.js'
import { ConfirmProvider } from './View/Component/ConfirmProvider.js'
import { TooltipProvider } from './Provider/TooltipManager.js'
import { CreateOrderCommonPage } from 'View/Page/HomePage/01-CreateOrderPage/CreateOrderCommonPage.tsx'
import { UppyProvider } from 'Provider/UppyProvider.tsx'
import PrintableReportPage from 'View/Page/OrderManagementPage/PrintableReportPage.tsx'
import { DialogPortalProvider } from './Provider/DialogPortalContext.js'
import { TourLanding } from './View/HelpTour/TourLanding.js'
import { MetadataManual } from './View/Page/MetadataManual.js'
import { GbifRedirectPage } from './View/Page/Gbif/GbifRedirectPage.js'
import { ImprintPage } from './View/Page/Legal/ImprintPage.js'
import { TextPage } from './View/Layout/TextPage.js'
import { PrivacyPolicyPage } from './View/Page/Legal/PrivacyPolicyPage.js'
import { TermsOfUsePage } from './View/Page/Legal/TermsOfServicePage.js'
import RegistrationPage from './View/Page/Registration/RegistrationPage.js'
import { BillingPage } from './View/Page/BillingPage/BillingPage.js'
import { GlobalModalHost } from './View/Modal/GlobalModalHost.js'
import { ModalListenerProvider } from './View/Modal/ModulListenerContext.js'
import { LayoutShell } from './View/Layout/PageLayout/LayoutShell.js'
import { AdminStatsPage } from './View/Page/AdminStatsPage/AdminStatsPage.js'
import { StatsMetricPage } from './View/Page/AdminStatsPage/StatsMetricPage.js'
import { AdminSpinPage } from './View/Page/AdminSpinPage/AdminSpinPage.js'
import { SpinCohesionPage } from './View/Page/AdminSpinPage/SpinCohesionPage.js'
import { SpinComparePage } from './View/Page/AdminSpinPage/SpinComparePage.js'
import { SpinReferencePage } from './View/Page/AdminSpinPage/SpinReferencePage.js'
import { SpinReferenceDetailPage } from './View/Page/AdminSpinPage/SpinReferenceDetailPage.js'
import { SpinLeaderboardPage } from './View/Page/AdminSpinPage/SpinLeaderboardPage.js'
import { SpinSingletonsPage } from './View/Page/AdminSpinPage/SpinSingletonsPage.js'
import { SpinDefinitionsPage } from './View/Page/AdminSpinPage/SpinDefinitionsPage.js'
import { SpinExportPage } from './View/Page/AdminSpinPage/SpinExportPage.js'
import { SpinDriftPage } from './View/Page/AdminSpinPage/SpinDriftPage.js'
import { SpinFlowPage } from './View/Page/AdminSpinPage/SpinFlowPage.js'
import { SpinGrowthPage } from './View/Page/AdminSpinPage/SpinGrowthPage.js'
import { SpinDetailPage } from './View/Page/AdminSpinPage/SpinDetailPage.js'
import { SpinSummaryPage } from './View/Page/SpinPage/SpinSummaryPage.js'
import { SpinLandingPage } from './View/Page/SpinPage/SpinLandingPage.js'
import { NewsListPage } from './View/Page/NewsPage/NewsListPage.js'
import { NewsArticlePage } from './View/Page/NewsPage/NewsArticlePage.js'
import { ColorCodePage } from './View/Page/ColorCodePage/ColorCodePage.js'
import { IdentityLabPage } from './View/Page/ColorCodePage/IdentityLab/IdentityLabPage.js'
import VizLoaderPage from './View/Component/ConfigurableVisualization/Workspace/VizLoaderPage.js'
import VizLoaderOrderPage from './View/Component/ConfigurableVisualization/Workspace/VizLoaderOrderPage.js'
import { TooltipLocationBridge } from 'Provider/TooltipLocationBridge.tsx'
import { colorCodeRouteFeatureAccess, projectsRouteFeatureAccess, spinRouteFeatureAccess } from './Model/index.js'
import { orderWizardRoute } from './View/Page/OrderWizard/module.tsx'
import { RouteErrorBoundary } from './View/Component/ErrorScreen/RouteErrorBoundary.js'
import { ErrorScreen } from './View/Component/ErrorScreen/ErrorScreen.js'

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter)

const Krona = lazy(() =>
  import('./View/Page/OrderManagementPage/Modals/Krona/KronaEmbedPage.tsx').then((module) => ({ default: module.KronaEmbedPage }))
)

// Old visualisation links, including permalinks with legacy query state, land on the workspace's default view.
const RedirectWithId = ({ to }: { to: (id: string) => string }) => {
  const { id } = useParams<{ id: string }>()
  return <Navigate replace to={to(id ?? '')} />
}

export const AppWithStoreWrapper = () => {
  const user = useGetAuthenticatedUser()

  const router = sentryCreateBrowserRouter([
    {
      errorElement: <RouteErrorBoundary />,
      element: (
        <>
          {' '}
          <TooltipLocationBridge /> <Outlet />{' '}
        </>
      ),
      children: [
        {
          path: '/register',
          element: (
            <>
              <GlobalModalHost />
              <RegistrationPage />
            </>
          )
        },
        {
          path: '/',
          element: <LoginPage />
        },
        {
          path: '/metadata-manual',
          element: <MetadataManual />
        },
        {
          path: '/gbif/:species',
          element: <GbifRedirectPage />
        },
        {
          path: '/order/:id/print-report',
          element: <PrintableReportPage />
        },
        {
          path: '/orders',
          element: <LayoutShell useFullWidth={true} />,
          children: [
            {
              path: 'confirm-postprocessing',
              element: <ConfirmPostprocessingPage />
            }
          ]
        },
        {
          path: '/legal/',
          element: <LayoutShell useFullWidth={true} />,
          children: [
            {
              element: <TextPage />,
              children: [
                {
                  path: 'imprint',
                  element: <ImprintPage />
                },
                {
                  path: 'tos',
                  element: <TermsOfUsePage />
                },
                {
                  path: 'privacy',
                  element: <PrivacyPolicyPage />
                }
              ]
            }
          ]
        },
        {
          element: <LayoutShell />,
          children: [
            {
              path: '/home',
              element: <HomePage />
            },
            {
              path: '/createOrderLab',
              element: <CreateOrderCommonPage mode={'lab'} />
            },
            orderWizardRoute(user || null),
            {
              path: '/tour',
              element: <TourLanding />
            },
            {
              path: '/order_management',
              element: <OrderManagementPage />
            },
            {
              path: '/projects',
              element: (
                <ProtectedRoute user={user || null}>
                  <ProjectManagementPage />
                </ProtectedRoute>
              )
            },
            {
              path: '/admin/stats',
              element: (
                <ProtectedRoute user={user || null}>
                  <AdminStatsPage />
                </ProtectedRoute>
              )
            },
            {
              path: '/admin/stats/m/:id',
              element: (
                <ProtectedRoute user={user || null}>
                  <StatsMetricPage />
                </ProtectedRoute>
              )
            },
            {
              path: '/admin/spin',
              element: (
                <ProtectedRoute user={user || null}>
                  <AdminSpinPage />
                </ProtectedRoute>
              )
            },
            {
              path: '/admin/spin/cohesion',
              element: (
                <ProtectedRoute user={user || null}>
                  <SpinCohesionPage />
                </ProtectedRoute>
              )
            },
            {
              path: '/admin/spin/compare',
              element: (
                <ProtectedRoute user={user || null}>
                  <SpinComparePage />
                </ProtectedRoute>
              )
            },
            {
              path: '/admin/spin/reference',
              element: (
                <ProtectedRoute user={user || null}>
                  <SpinReferencePage />
                </ProtectedRoute>
              )
            },
            {
              // Drill-down for one reference id; refId is percent-encoded (BOLD keys contain a colon).
              path: '/admin/spin/reference/:refKind/:refId',
              element: (
                <ProtectedRoute user={user || null}>
                  <SpinReferenceDetailPage />
                </ProtectedRoute>
              )
            },
            {
              path: '/admin/spin/singletons',
              element: (
                <ProtectedRoute user={user || null}>
                  <SpinSingletonsPage />
                </ProtectedRoute>
              )
            },
            {
              path: '/admin/spin/leaderboard',
              element: (
                <ProtectedRoute user={user || null}>
                  <SpinLeaderboardPage />
                </ProtectedRoute>
              )
            },
            {
              path: '/admin/spin/export',
              element: (
                <ProtectedRoute user={user || null}>
                  <SpinExportPage />
                </ProtectedRoute>
              )
            },
            {
              path: '/admin/spin/about',
              element: (
                <ProtectedRoute user={user || null}>
                  <SpinDefinitionsPage />
                </ProtectedRoute>
              )
            },
            {
              path: '/admin/spin/growth',
              element: (
                <ProtectedRoute user={user || null}>
                  <SpinGrowthPage />
                </ProtectedRoute>
              )
            },
            {
              path: '/admin/spin/drift',
              element: (
                <ProtectedRoute user={user || null}>
                  <SpinDriftPage />
                </ProtectedRoute>
              )
            },
            {
              path: '/admin/spin/flow',
              element: (
                <ProtectedRoute user={user || null}>
                  <SpinFlowPage />
                </ProtectedRoute>
              )
            },
            {
              path: '/admin/spin/s/:spinId',
              element: (
                <ProtectedRoute user={user || null}>
                  <SpinDetailPage />
                </ProtectedRoute>
              )
            },
            {
              path: '/spins',
              element: (
                <ProtectedRoute user={user || null} featureAccessRequired={spinRouteFeatureAccess}>
                  <SpinLandingPage />
                </ProtectedRoute>
              )
            },
            {
              path: '/news',
              element: (
                <ProtectedRoute user={user || null}>
                  <NewsListPage />
                </ProtectedRoute>
              )
            },
            {
              path: '/news/:slug',
              element: (
                <ProtectedRoute user={user || null}>
                  <NewsArticlePage />
                </ProtectedRoute>
              )
            },
            {
              path: '/colorcode/lab',
              element: (
                <ProtectedRoute user={user || null} featureAccessRequired={colorCodeRouteFeatureAccess}>
                  <IdentityLabPage />
                </ProtectedRoute>
              )
            },
            {
              path: '/colorcode',
              element: (
                <ProtectedRoute user={user || null} featureAccessRequired={colorCodeRouteFeatureAccess}>
                  <ColorCodePage />
                </ProtectedRoute>
              )
            },
            {
              path: '/spins/:spinId',
              element: (
                <ProtectedRoute user={user || null} featureAccessRequired={spinRouteFeatureAccess}>
                  <SpinSummaryPage />
                </ProtectedRoute>
              )
            },
            {
              path: '/createOrderCustomer',
              element: <CreateOrderCommonPage mode={'customer'} />
            },
            {
              path: '/order/:id',
              element: <OrderParamLoaderView />
            },
            {
              path: '/projects/:id',
              element: (
                <ProtectedRoute user={user || null} featureAccessRequired={projectsRouteFeatureAccess}>
                  <ProjectParamLoaderView />
                </ProtectedRoute>
              )
            },
            {
              path: '/invite',
              element: (
                <ProtectedRoute user={user || null}>
                  <InvitesPage />
                </ProtectedRoute>
              )
            },
            {
              path: '/invite/:inviteId',
              element: (
                <ProtectedRoute user={user || null}>
                  <InviteDetailPage />
                </ProtectedRoute>
              )
            },
            {
              path: '/organization/:id',
              element: <OrganizationPage />
            },
            {
              path: '/marketplace',
              element: (
                <ProtectedRoute user={user || null}>
                  <OrganizationSearchPage />
                </ProtectedRoute>
              )
            },
            {
              path: '/billing',
              element: (
                <ProtectedRoute user={user || null}>
                  <BillingPage />
                </ProtectedRoute>
              )
            }
          ]
        },
        {
          element: <LayoutShell useFullWidth={true} />,
          children: [
            {
              path: '/order/:id/otus',
              element: <OtuDataTableView type="ORDER" />
            },
            {
              path: '/projects/:id/otus',
              element: (
                <ProtectedRoute user={user || null} featureAccessRequired={projectsRouteFeatureAccess}>
                  <OtuDataTableView type="PROJECT" />
                </ProtectedRoute>
              )
            }
          ]
        },
        {
          element: <LayoutShell useFullWidth={true} appMode={true} />,
          // Chat/agent routes only exist when the feature is enabled at build time (mirrors the backend's
          // `mcp` env opt-in). With CHAT_ENABLED false this folds to [], so ChatPage is tree-shaken out.
          children: Environment.CHAT_ENABLED
            ? [
                {
                  path: '/order/:id/chat',
                  element: (
                    <ProtectedRoute user={user || null} roleRequired="CHAT_BETA">
                      <ChatPage type="ORDER" />
                    </ProtectedRoute>
                  )
                },
                {
                  path: '/projects/:id/chat',
                  element: (
                    <ProtectedRoute user={user || null} roleRequired="CHAT_BETA">
                      <ChatPage type="PROJECT" />
                    </ProtectedRoute>
                  )
                }
              ]
            : []
        },
        {
          // The workspace scrolls its own canvas, so it gets the viewport-height shell: no footer, no page scroll.
          element: <LayoutShell useFullWidth={true} appMode={true} />,
          children: [
            {
              path: '/order/:id/visualisation',
              element: <RedirectWithId to={(id) => `/order/workspace/${id}`} />
            },
            {
              path: '/projects/:id/visualisation',
              element: <RedirectWithId to={(id) => `/project/workspace/${id}`} />
            },
            {
              path: '/project/workspace/:id',
              element: <VizLoaderPage />
            },
            {
              path: '/order/workspace/:id',
              element: <VizLoaderOrderPage />
            }
          ]
        },
        {
          path: '/krona-embed/:id',
          element: (
            <Suspense fallback={<div>loading Krona Chart...</div>}>
              <Krona />
            </Suspense>
          )
        }
      ]
    }
  ])

  const dispatch = useDispatch()
  useEffect(() => {
    dispatch(Actions[authenticationSlice.name].updateUserIfPresent())

    dispatch(
      connectToServer({
        url: Environment.API_URL.replace('https://', 'wss://').replace('http://', 'ws://') + '/ws/mau/orders',
        reconnectionTimeout: 3000
      })
    )
  }, [dispatch])

  return (
    <Sentry.ErrorBoundary fallback={({ eventId, resetError }) => <ErrorScreen eventId={eventId} onRetry={resetError} />}>
      <ServerResponseProvider>
        <ModalListenerProvider>
          <DialogPortalProvider>
            <TooltipProvider>
              <ConfirmProvider>
                <UppyProvider>
                  <OrganizationProvider>
                    <RouterProvider router={router} />
                  </OrganizationProvider>
                </UppyProvider>
              </ConfirmProvider>
            </TooltipProvider>
          </DialogPortalProvider>
        </ModalListenerProvider>
      </ServerResponseProvider>
    </Sentry.ErrorBoundary>
  )
}
